import { useState } from "react";
import { Zap, Download } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminFounding = ({ stats, foundingMembers, getAuthHeaders, fetchData }) => {
  const [foundingFilter, setFoundingFilter] = useState("all");

  const handleToggleFounding = async (businessId) => {
    try {
      await axios.put(`${API}/admin/founding-members/${businessId}/toggle`, {}, getAuthHeaders());
      toast.success("Founding status updated");
      fetchData();
    } catch { toast.error("Failed to update founding status"); }
  };

  const handleExportFounding = async () => {
    try {
      const res = await axios.get(`${API}/admin/founding-members/export`, { ...getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "founding_members.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to export founding members"); }
  };

  const filtered = foundingMembers.filter(
    m => foundingFilter === "all" || (m.founding_category || "business") === foundingFilter
  );

  return (
    <div className="space-y-6">
      {/* Spots Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { key: "business", label: "SMB Founding", color: "#14B8A6" },
          { key: "corporate", label: "Corporate Founding", color: "#6366F1" },
          { key: "industrial", label: "Industrial Founding", color: "#DC2626" },
        ].map(({ key, label, color }) => {
          const breakdown = stats.founding_breakdown || {};
          const claimed = breakdown[key] || 0;
          const limit = breakdown.limits?.[key] || 0;
          const pct = limit > 0 ? Math.round((claimed / limit) * 100) : 0;
          return (
            <div key={key} className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5" data-testid={`founding-stat-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#1A1915]">{label}</span>
                <Zap className="w-4 h-4" style={{ color }} />
              </div>
              <div className="text-3xl font-bold" style={{ color }}>{claimed} <span className="text-sm font-normal text-[#5C5A52]">/ {limit}</span></div>
              <div className="mt-2 h-2 bg-[#EFEDE5] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
              <p className="text-xs text-[#5C5A52] mt-1">{limit - claimed} spot{limit - claimed !== 1 ? "s" : ""} remaining</p>
            </div>
          );
        })}
      </div>

      {/* Filter + Export */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {["all", "business", "corporate", "industrial"].map(f => (
            <Button key={f} size="sm" variant={foundingFilter === f ? "default" : "outline"}
              className={foundingFilter === f ? "bg-[#14B8A6]" : ""}
              onClick={() => setFoundingFilter(f)} data-testid={`founding-filter-${f}`}>
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={handleExportFounding} data-testid="export-founding-btn">
          <Download className="w-4 h-4 mr-2" />Export CSV
        </Button>
      </div>

      {/* Members Table */}
      <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Town</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Founding Type</TableHead>
              <TableHead>Approved</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(m => {
              const fc = m.founding_category || "business";
              const badgeColor = fc === "corporate" ? "#6366F1" : fc === "industrial" ? "#DC2626" : "#14B8A6";
              return (
                <TableRow key={m.id}>
                  <TableCell>
                    <p className="font-medium text-[#1A1915]">{m.name}</p>
                    <p className="text-xs text-[#5C5A52]">{m.email}</p>
                  </TableCell>
                  <TableCell className="text-sm">{m.owner_name || "---"}</TableCell>
                  <TableCell className="text-sm">{m.town}</TableCell>
                  <TableCell className="text-sm">{m.category}</TableCell>
                  <TableCell className="text-sm">{m.membership_tier}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ backgroundColor: `${badgeColor}15`, color: badgeColor, border: `1px solid ${badgeColor}30` }}>
                      <Zap className="w-3 h-3" />
                      {fc === "corporate" ? "Corporate" : fc === "industrial" ? "Industrial" : "Business"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {m.is_approved ? <Badge className="bg-[#2C4A3B] text-white text-xs">Approved</Badge>
                      : <Badge variant="secondary" className="text-xs">Pending</Badge>}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-[#B85C38]"
                      onClick={() => handleToggleFounding(m.id)} data-testid={`toggle-founding-${m.id}`}>
                      Remove Founding
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#5C5A52] py-8">
                  No founding members {foundingFilter !== "all" ? `in ${foundingFilter} category` : "yet"}.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
